import type * as borogove from "borogove";
import { Button } from "@chatscope/chat-ui-kit-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, CircularProgress } from "@mui/material";
import {
	faChevronDown,
	faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useOutletContext } from "react-router";
import { useCollapsedAttachments } from "../hooks/use-collapsed-attachments";
import { assertNever } from "../assert-never";
import {
	useAttachmentFetch,
	Status,
	type FetchedAttachment,
} from "../hooks/use-attachment-fetch";

export const MessageAttachments = ({
	client,
	chat,
	message,
	childMessage,
}: {
	client: borogove.Client;
	chat: borogove.Chat;
	message: borogove.ChatMessage;
	childMessage?: borogove.ChatMessage;
}) => {
	const { persistence } = useOutletContext<{
		persistence: borogove.persistence.KeyValueStore;
	}>();
	const [collapsed, setCollapsed] = useCollapsedAttachments(
		persistence,
		message,
		childMessage,
	);
	const [fetchState, fetchAttachments] = useAttachmentFetch(
		client,
		chat,
		message,
	);

	const fetchButton = (count: number) => (
		<Button
			onClick={(event) => {
				event.stopPropagation();
				fetchAttachments();
			}}
		>
			Fetch {attachmentLabel(count, fetchState.fetched.size > 0)}
		</Button>
	);

	const renderFetchControls = () => {
		switch (fetchState.status) {
			case Status.Fetching:
				return <CircularProgress size={24} aria-label="Fetching attachments" />;
			case Status.Unfetched:
				return fetchButton(fetchState.unfetched.size);
			case Status.Failed:
				return (
					<>
						<Alert severity="error">
							Could not fetch all attachments. Try again.
						</Alert>
						{fetchButton(fetchState.failed.size)}
					</>
				);
			case Status.Fetched:
				return null;
			default:
				return assertNever(fetchState);
		}
	};

	return (
		<div className="message-attachments">
			{fetchState.fetched.size > 0 && (
				<>
					<button
						type="button"
						className="message-attachments-toggle"
						title={collapsed ? "Show attachments" : "Hide attachments"}
						aria-expanded={!collapsed}
						onClick={(event) => {
							event.stopPropagation();
							void setCollapsed(!collapsed);
						}}
					>
						{attachmentLabel(fetchState.fetched.size)}
						<FontAwesomeIcon
							icon={collapsed ? faChevronRight : faChevronDown}
						/>
					</button>
					{!collapsed && (
						<div>
							{[...fetchState.fetched].map(
								([attachment, fetchedAttachment], i) => (
									<AttachmentContent
										key={i}
										attachment={attachment}
										fetchedAttachment={fetchedAttachment}
									/>
								),
							)}
						</div>
					)}
				</>
			)}
			{renderFetchControls()}
		</div>
	);
};

const AttachmentContent = ({
	attachment,
	fetchedAttachment,
}: {
	attachment: borogove.ChatAttachment;
	fetchedAttachment: FetchedAttachment;
}) => {
	const { uri, embeddable } = fetchedAttachment;
	const href =
		attachment.name && uri.startsWith("/.well-known/ni/")
			? `${uri}?name=${encodeURIComponent(attachment.name)}`
			: uri;
	if (!embeddable) return <a href={href}>{attachment.name || "attachment"}</a>;

	if (attachment.mime?.indexOf("image/") === 0) {
		return (
			<a href={href}>
				<img
					alt={attachment.name || "attachment"}
					crossOrigin="anonymous"
					src={uri}
					style={{ height: "15em", objectFit: "scale-down" }}
				/>
			</a>
		);
	} else if (attachment.mime?.indexOf("audio/") === 0) {
		return <audio crossOrigin="anonymous" src={uri} controls></audio>;
	} else if (attachment.mime?.indexOf("video/") === 0) {
		return (
			<video
				crossOrigin="anonymous"
				src={uri}
				style={{ height: "15em", objectFit: "scale-down" }}
				controls
			></video>
		);
	} else {
		return <a href={href}>{attachment.name || "attachment"}</a>;
	}
};

const attachmentLabel = (count: number, remaining = false) =>
	`${count}${remaining ? " remaining" : ""} attachment${count === 1 ? "" : "s"}`;
